import { AsyncSubject, Observable } from 'rxjs';
import { Auth } from '@aws-amplify/auth';
import { AuthOptions } from '@aws-amplify/auth/lib-esm/types';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { usePortalName } from '../hooks/usePortalName';

const { __environment__ } = window as any;

const region = __environment__.REACT_APP_AWS_USER_POOL_REGION;
const userPool = __environment__.REACT_APP_AWS_USER_POOL_ID;
const clientId = __environment__.REACT_APP_AWS_CLIENT_ID;
const domain = __environment__.REACT_APP_AWS_DOMAIN;
const signinUri = __environment__.REACT_APP_AWS_SIGNIN_URI;
const signoutUri = __environment__.REACT_APP_AWS_SIGNOUT_URI;

export interface Tokens {
  accessToken?: string;
  idToken?: string;
  accessTokenExpirationMs?: number;
}

/**
 * Bootstrap route opened in the login popup. It is a page of this app (never
 * sent to Cognito), so it does not need to be whitelisted anywhere. It starts
 * the Amplify OAuth flow itself so that the PKCE code verifier, the `state`
 * value and the code exchange all happen inside the popup's storage bucket.
 * See docs/popup-login-plan.md.
 */
export const POPUP_LOGIN_PATH = '/popupLogin';

/** Query parameter carrying the federated provider into the bootstrap route. */
export const POPUP_LOGIN_PROVIDER_PARAM = 'provider';

const POPUP_FEATURES =
  'width=520,height=680,menubar=no,toolbar=no,location=no,status=no';
const POPUP_CLOSE_POLL_MS = 500;
const POPUP_TIMEOUT_MS = 5 * 60 * 1000;
const SESSION_POLL_INTERVAL_MS = 250;
const SESSION_POLL_TIMEOUT_MS = 15 * 1000;

/**
 * True when this document is rendered inside an iframe (for example the Amazon
 * Connect agent workspace). Reading `window.top` throws when the top window is
 * cross-origin, which by itself means we are framed.
 *
 * Shared helper — docs/awsc-sdk-integration.md reuses this; do not duplicate.
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/** True when this document was opened via `window.open` by our own app. */
export const isInPopup = (): boolean =>
  window.opener != null && window.opener !== window;

export const COGNITO_LOGIN_SUCCESS = 'COGNITO_LOGIN_SUCCESS' as const;
export const COGNITO_LOGIN_FAILURE = 'COGNITO_LOGIN_FAILURE' as const;

export interface CognitoLoginSuccessMessage {
  type: typeof COGNITO_LOGIN_SUCCESS;
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface CognitoLoginFailureMessage {
  type: typeof COGNITO_LOGIN_FAILURE;
  message: string;
}

export type CognitoLoginMessage =
  | CognitoLoginSuccessMessage
  | CognitoLoginFailureMessage;

/**
 * Runtime validation for `MessageEvent.data`. The origin check alone is not
 * enough — the payload is still untrusted input and its shape must be verified
 * before any field is read.
 */
export const isCognitoLoginMessage = (
  data: unknown,
): data is CognitoLoginMessage => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const message = data as Record<string, unknown>;
  if (message.type === COGNITO_LOGIN_SUCCESS) {
    return (
      typeof message.idToken === 'string' &&
      typeof message.accessToken === 'string' &&
      typeof message.refreshToken === 'string'
    );
  }
  if (message.type === COGNITO_LOGIN_FAILURE) {
    return typeof message.message === 'string';
  }
  return false;
};

export type PopupLoginErrorCode =
  | 'POPUP_BLOCKED'
  | 'POPUP_CLOSED'
  | 'POPUP_TIMEOUT'
  | 'LOGIN_FAILED';

export interface PopupLoginError extends Error {
  code: PopupLoginErrorCode;
}

/**
 * `Error` is not subclassed here on purpose: the TypeScript target is es5, and
 * subclassing built-ins under es5 breaks `instanceof`. Callers branch on
 * `error.code` instead.
 */
const popupLoginError = (
  code: PopupLoginErrorCode,
  message: string,
): PopupLoginError => {
  const error = new Error(message) as PopupLoginError;
  error.code = code;
  return error;
};

export const isPopupLoginError = (error: unknown): error is PopupLoginError =>
  error instanceof Error && typeof (error as PopupLoginError).code === 'string';

/** Notified after a popup login has been fully applied to this window. */
export type PopupLoginSuccessListener = () => void;

class AuthService {
  private loggedIn = false;
  private inFlightTokensSubject: AsyncSubject<Tokens> | null = null;

  /**
   * Resolved from the same source as `Auth.configure` so that
   * `applyPopupSession` writes tokens for the pool this window actually uses.
   * Never hardcode these — they differ per environment and per portal.
   */
  private readonly userPoolId: string;
  private readonly clientId: string;

  private popupSuccessListeners = new Set<PopupLoginSuccessListener>();
  private popupFailureListeners = new Set<(error: PopupLoginError) => void>();
  /** Set while a login popup is open, so messages from other windows are ignored. */
  private expectedPopup: Window | null = null;

  constructor() {
    const portalName = usePortalName();
    if (portalName) {
      const config = this.getPortalConfiguration(portalName);
      this.userPoolId = config.userPoolId ?? '';
      this.clientId = config.userPoolWebClientId ?? '';
      Auth.configure(config);
    } else {
      this.userPoolId = userPool;
      this.clientId = clientId;
      Auth.configure({
        region: region,
        userPoolId: userPool,
        userPoolWebClientId: clientId,
        oauth: {
          domain: domain,
          scope: [
            'email',
            'profile',
            'openid',
            'aws.cognito.signin.user.admin',
          ],
          redirectSignIn: signinUri,
          redirectSignOut: signoutUri,
          responseType: 'code',
        },
      });
    }

    Auth.currentSession().then((session) => {
      this.loggedIn = session.isValid();
    });

    // A single listener owns validation and session application, so a popup
    // login is never applied twice and subscribers always run after the
    // session is in place.
    window.addEventListener('message', this.handlePopupMessage);
  }

  private handlePopupMessage = async (event: MessageEvent): Promise<void> => {
    if (event.origin !== window.location.origin) {
      return;
    }
    // Only accept messages while a popup login is in flight, and only from the
    // specific window we opened. Without the null check any same-origin window
    // holding a reference to this one (another tab, for example) could forge a
    // success message and have attacker-supplied tokens persisted here.
    if (!this.expectedPopup || event.source !== this.expectedPopup) {
      return;
    }
    if (!isCognitoLoginMessage(event.data)) {
      return;
    }

    if (event.data.type === COGNITO_LOGIN_FAILURE) {
      const error = popupLoginError('LOGIN_FAILED', event.data.message);
      this.popupFailureListeners.forEach((listener) => listener(error));
      return;
    }

    try {
      await this.applyPopupSession(event.data);
      this.popupSuccessListeners.forEach((listener) => listener());
    } catch (err) {
      const error = popupLoginError(
        'LOGIN_FAILED',
        err instanceof Error ? err.message : 'Login failed',
      );
      this.popupFailureListeners.forEach((listener) => listener(error));
    }
  };

  /**
   * Subscribe to popup logins that completed in this window. Returns an
   * unsubscribe function.
   */
  public onPopupLoginSuccess(listener: PopupLoginSuccessListener): () => void {
    this.popupSuccessListeners.add(listener);
    return () => {
      this.popupSuccessListeners.delete(listener);
    };
  }

  /**
   * Subscribe to popup logins that failed. Returns an unsubscribe function.
   */
  public onPopupLoginFailure(
    listener: (error: PopupLoginError) => void,
  ): () => void {
    this.popupFailureListeners.add(listener);
    return () => {
      this.popupFailureListeners.delete(listener);
    };
  }

  private getPortalRegion(portalName: string): string {
    const envName = `REACT_APP_AWS_USER_POOL_REGION_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal
      ? envVal
      : __environment__.REACT_APP_AWS_USER_POOL_REGION_EXTERNAL;
  }

  private getPortalUserPool(portalName: string): string {
    const envName = `REACT_APP_AWS_USER_POOL_ID_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal
      ? envVal
      : __environment__.REACT_APP_AWS_USER_POOL_ID_EXTERNAL;
  }

  private getPortalClientID(portalName: string): string {
    const envName = `REACT_APP_AWS_CLIENT_ID_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal ? envVal : __environment__.REACT_APP_AWS_CLIENT_ID_EXTERNAL;
  }

  private getPortalDomain(portalName: string): string {
    const envName = `REACT_APP_AWS_DOMAIN_${portalName.toUpperCase()}`;
    const envVal = __environment__[envName];
    return envVal ? envVal : __environment__.REACT_APP_AWS_DOMAIN_EXTERNAL;
  }

  private getPortalConfiguration(portalName: string): AuthOptions {
    return {
      region: this.getPortalRegion(portalName),
      userPoolId: this.getPortalUserPool(portalName),
      userPoolWebClientId: this.getPortalClientID(portalName),
      oauth: {
        domain: this.getPortalDomain(portalName),
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: this.getExternalSigninUrl(portalName),
        redirectSignOut: this.getExternalSignoutUrl(portalName),
        responseType: 'code',
      },
    };
  }

  private getExternalUrl(name: string) {
    return __environment__[name];
  }

  private getExternalSigninUrl(portalName: string) {
    return this.getExternalUrl(
      `REACT_APP_AWS_SIGNIN_URI_EXTERNAL_${portalName.toUpperCase()}`,
    );
  }

  private getExternalSignoutUrl(portalName: string) {
    return this.getExternalUrl(
      `REACT_APP_AWS_SIGNOUT_URI_EXTERNAL_${portalName.toUpperCase()}`,
    );
  }

  /**
   * Awaitable session refresh. `isLoggedIn()` is read synchronously during
   * render, so callers that need `loggedIn` to be current before they trigger a
   * re-render must await this rather than use the fire-and-forget
   * `checkSession`.
   */
  public async refreshSession(): Promise<boolean> {
    try {
      const session = await Auth.currentSession();
      this.loggedIn = session.isValid();
    } catch (_err) {
      this.loggedIn = false;
    }
    return this.loggedIn;
  }

  private checkSession() {
    void this.refreshSession();
  }

  public getFederatedCustomProvider(portalName: string | null): string | null {
    if (portalName) {
      const envName = `REACT_APP_AWS_CUSTOM_PROVIDER_${portalName.toUpperCase()}`;
      const envVal = __environment__[envName];
      return envVal ? envVal : null;
    }
    return __environment__.REACT_APP_AWS_CUSTOM_PROVIDER;
  }

  public getUserInfo(): Observable<{
    name: string;
    email: string;
  }> {
    const subject = new AsyncSubject<{
      name: string;
      email: string;
    }>();
    Auth.currentAuthenticatedUser().then((userInfo) => {
      subject.next({
        name: `${userInfo.attributes.given_name} ${userInfo.attributes.family_name}`,
        email: userInfo.attributes.email,
      });
      subject.complete();
    });
    return subject;
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  public getTokens(): Observable<Tokens> {
    if (this.inFlightTokensSubject) {
      return this.inFlightTokensSubject;
    }

    const subject = new AsyncSubject<Tokens>();
    this.inFlightTokensSubject = subject;

    Auth.currentSession()
      .then((session) => {
        const accessToken = session?.getAccessToken();
        const tokenData: Tokens = {
          accessToken: accessToken?.getJwtToken(),
          idToken: session?.getIdToken()?.getJwtToken(),
        };

        const accessTokenExpirationSeconds = accessToken?.getExpiration?.();
        if (typeof accessTokenExpirationSeconds === 'number') {
          tokenData.accessTokenExpirationMs =
            accessTokenExpirationSeconds * 1000;
        }

        subject.next({
          ...tokenData,
        });
        subject.complete();
      })
      .catch(() => {
        subject.next({});
        subject.complete();
      })
      .finally(() => {
        this.inFlightTokensSubject = null;
      });

    return subject;
  }

  public memberLogin(
    username: string,
    password: string,
    isProvider: boolean = true,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      Auth.configure({
        authenticationFlowType: isProvider
          ? 'USER_PASSWORD_AUTH'
          : 'USER_SRP_AUTH',
      });

      Auth.signIn(username, password)
        .then((value) => {
          if (value.challengeName === 'NEW_PASSWORD_REQUIRED') {
            resolve(value);
          } else {
            this.loggedIn = true;
            resolve(value);
          }
        })
        .catch((err) => {
          reject(err?.message || 'Login failed');
        });
    });
  }

  public async handleLoginCallback(_url: string): Promise<void> {
    return new Promise(async (resolve) => {
      Auth.currentSession()
        .then((session) => {
          this.loggedIn = session.isValid();
          // This is required for the portal so it can force
          // the application router to be initiated again.
          window.location.href = '/';
        })
        .finally(() => resolve());
    });
  }

  public loginFederated(customProvider: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.federatedSignIn({
        customProvider,
      })
        .then((value) => {
          this.checkSession();
          resolve();
        })
        .catch((err) => {
          reject('Login failed');
        });
    });
  }

  /**
   * Popup variant of `loginFederated`, for use when the app runs inside an
   * iframe (Amazon Connect agent workspace). The identity provider refuses to
   * be framed (`X-Frame-Options: DENY`), so the flow is moved into a top-level
   * popup window.
   *
   * Must be called from a user gesture — browsers block `window.open`
   * otherwise. Resolves only once the resulting session has been applied to
   * this window, so callers may navigate immediately afterwards.
   */
  public loginFederatedPopup(customProvider: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = `${POPUP_LOGIN_PATH}?${POPUP_LOGIN_PROVIDER_PARAM}=${encodeURIComponent(
        customProvider,
      )}`;
      const popup = window.open(url, 'cognitoLogin', POPUP_FEATURES);

      if (!popup) {
        reject(
          popupLoginError(
            'POPUP_BLOCKED',
            'The login window was blocked by the browser.',
          ),
        );
        return;
      }

      this.expectedPopup = popup;

      let settled = false;
      let closePoll: ReturnType<typeof setInterval> | undefined;
      let timeout: ReturnType<typeof setTimeout> | undefined;
      let unsubscribeSuccess: () => void = () => undefined;
      let unsubscribeFailure: () => void = () => undefined;

      const settle = (complete: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        unsubscribeSuccess();
        unsubscribeFailure();
        if (closePoll !== undefined) {
          clearInterval(closePoll);
        }
        if (timeout !== undefined) {
          clearTimeout(timeout);
        }
        this.expectedPopup = null;
        complete();
      };

      unsubscribeSuccess = this.onPopupLoginSuccess(() => settle(resolve));
      unsubscribeFailure = this.onPopupLoginFailure((error) =>
        settle(() => reject(error)),
      );

      closePoll = setInterval(() => {
        if (popup.closed) {
          settle(() =>
            reject(
              popupLoginError(
                'POPUP_CLOSED',
                'The login window was closed before login completed.',
              ),
            ),
          );
        }
      }, POPUP_CLOSE_POLL_MS);

      timeout = setTimeout(() => {
        try {
          popup.close();
        } catch (_err) {
          // Closing is best effort only.
        }
        settle(() =>
          reject(popupLoginError('POPUP_TIMEOUT', 'The login timed out.')),
        );
      }, POPUP_TIMEOUT_MS);
    });
  }

  /**
   * Persists tokens received from the login popup into *this* window's Amplify
   * storage.
   *
   * The popup is a top-level document and therefore writes to the first-party
   * storage bucket, while an iframe in the agent workspace reads a partitioned
   * bucket. The two are not the same, so the tokens have to be transferred
   * explicitly and re-applied here.
   */
  public async applyPopupSession(
    message: CognitoLoginSuccessMessage,
  ): Promise<void> {
    const pool = new CognitoUserPool({
      UserPoolId: this.userPoolId,
      ClientId: this.clientId,
    });
    const idToken = new CognitoIdToken({ IdToken: message.idToken });
    const session = new CognitoUserSession({
      IdToken: idToken,
      AccessToken: new CognitoAccessToken({
        AccessToken: message.accessToken,
      }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: message.refreshToken,
      }),
    });

    const payload = idToken.decodePayload();
    const username = (payload['cognito:username'] ?? payload.sub) as string;
    if (!username) {
      throw new Error('Could not determine the user from the id token.');
    }

    const user = new CognitoUser({ Username: username, Pool: pool });
    // Also writes the `LastAuthUser` key, which is what lets Amplify pick this
    // session up again via Auth.currentSession().
    user.setSignInUserSession(session);

    await this.refreshSession();
  }

  /**
   * Runs in the popup on the OAuth callback route: hands the freshly minted
   * tokens to the opener and closes. Never redirects — that is the non-popup
   * path in `handleLoginCallback`.
   */
  public async completePopupLogin(): Promise<void> {
    const opener = window.opener as Window | null;
    try {
      const session = await this.waitForSession();
      const message: CognitoLoginSuccessMessage = {
        type: COGNITO_LOGIN_SUCCESS,
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
      };
      opener?.postMessage(message, window.location.origin);
    } catch (err) {
      const message: CognitoLoginFailureMessage = {
        type: COGNITO_LOGIN_FAILURE,
        message: err instanceof Error ? err.message : 'Login failed',
      };
      // Always report something — a silent popup leaves the opener's loading
      // state stuck until the close poll or the timeout fires.
      opener?.postMessage(message, window.location.origin);
    } finally {
      window.close();
    }
  }

  /**
   * Amplify exchanges the authorization code asynchronously once it sees the
   * `?code=` parameter, so the session is not guaranteed to exist on the first
   * tick. Poll briefly instead of failing on a race.
   */
  private async waitForSession(
    timeoutMs: number = SESSION_POLL_TIMEOUT_MS,
  ): Promise<CognitoUserSession> {
    const deadline = Date.now() + timeoutMs;
    let lastError: unknown;

    for (;;) {
      try {
        return (await Auth.currentSession()) as CognitoUserSession;
      } catch (err) {
        lastError = err;
      }
      if (Date.now() >= deadline) {
        throw lastError instanceof Error
          ? lastError
          : new Error('Timed out waiting for the login session.');
      }
      await new Promise((done) => setTimeout(done, SESSION_POLL_INTERVAL_MS));
    }
  }

  /**
   * Signs the user out and always resolves.
   *
   * It used to reject when `Auth.signOut()` failed, and none of the seven call sites handled that
   * rejection, so a failed sign-out left the user sitting in an authenticated UI with nothing
   * happening at all - the navigation to the login page is chained onto this promise. Being unable
   * to reach Cognito is not a reason to keep someone logged in, so the local session is discarded
   * either way and the caller can always navigate away.
   *
   * The underlying error is logged rather than swallowed, because the remote call failing is still
   * worth seeing in the console.
   */
  public logout(): Promise<void> {
    return Auth.signOut()
      .catch(async (err) => {
        console.error('Remote sign-out failed; discarding the local session anyway', err);
        // Amplify's local sign-out does not touch the network, so it still works when the hosted-UI
        // or revocation call above did not.
        try {
          const user = await Auth.currentAuthenticatedUser();
          user.signOut();
        } catch (localErr) {
          // No user to sign out locally - already effectively logged out.
          console.debug('No local session left to clear', localErr);
        }
      })
      .then(() => {
        this.checkSession();
      });
  }
}

const authService = new AuthService();

export default authService;
